import type { AgentMessagesResult, BestVaultOption, SupportedAsset, VaultDeploymentPrediction, VaultInfo } from "../types";
import { HttpClient } from "./HttpClient";

export interface PrepareVaultResponse {
  salt: string;
  homeChainId: number;
  predictions: VaultDeploymentPrediction[];
}

export class VaultApiService {
  constructor(private readonly http: HttpClient) {}

  async getVault(walletAddress: string): Promise<VaultInfo> {
    const response = await this.http.get<any>(
      `/api/v4/vault?walletAddress=${walletAddress}`,
    );
    const vault = response.vault ?? response.data?.vault ?? response.data ?? response;
    return {
      userVaultAddress: vault?.userVaultAddress ?? null,
      deploymentSalt: vault?.deploymentSalt ?? null,
      exists: Boolean(vault?.userVaultAddress),
      walletAddress: vault?.walletAddress ?? null,
      homeChainId: vault?.homeChainId ?? null,
      vaultVersion: vault?.vaultVersion ?? null,
      isActive: vault?.isActive,
      totalValueUSD: vault?.totalValueUSD ?? null,
      totalDepositedUSD: vault?.totalDepositedUSD ?? null,
      earned: vault?.earned ?? null,
      apyBreakdown: vault?.apyBreakdown ?? null,
      league: vault?.league ?? null,
      assets: vault?.assets ?? [],
      chainAddresses: vault?.chainAddresses ?? [],
    };
  }

  async prepare(homeChainId: number): Promise<PrepareVaultResponse> {
    const response = await this.http.post<any>("/api/v4/vault/prepare", {
      homeChainId,
    });
    const prepared = response.data ?? response;
    return {
      salt: prepared.salt,
      homeChainId: prepared.homeChainId,
      predictions: prepared.predictions ?? [],
    };
  }

  async confirm(input: {
    userVaultAddress: string;
    homeChainId: number;
    deploymentSalt: string;
    initialAssets: Array<Record<string, unknown>>;
  }): Promise<void> {
    await this.http.post("/api/v4/vault/confirm", input);
  }

  async getPending(): Promise<{
    deploymentSalt: string | null;
    chainId?: number;
    factoryAddress?: string;
    predictedVaultAddress?: string;
  } | null> {
    const response = await this.http.get<{
      deploymentSalt: string | null;
      chainId?: number;
      factoryAddress?: string;
      predictedVaultAddress?: string;
    } | null>(
      "/api/v4/vault/pending",
    );
    return (response as any)?.data ?? response;
  }

  async getSupportedAssets(chainId?: number): Promise<SupportedAsset[]> {
    const response = await this.http.get<any>("/api/v4/vault?walletAddress=0x");
    // The supported-asset catalog now lives under `vault.assets` (annotated with
    // the caller's holdings when a session cookie identifies them). Older
    // responses exposed it as a top-level `defaultAssets` array — fall back to
    // that, and to other historical shapes, for compatibility.
    const vault = response.vault ?? response.data?.vault ?? response.data ?? response;
    const sourceAssets: any[] =
      vault?.assets ??
      vault?.defaultAssets ??
      response.defaultAssets ??
      response.data?.defaultAssets ??
      [];
    const assets: SupportedAsset[] = sourceAssets.map((a) => ({
      assetAddress: a.assetAddress,
      assetSymbol: a.assetSymbol,
      assetDecimals: a.assetDecimals,
      chainId: a.chainId,
      chainStatus: a.chainStatus,
      currentAPY: a.currentAPY,
      nativeAPY: a.nativeAPY,
      merklAPY: a.merklAPY,
      leagueAPY: a.leagueAPY,
      apy7d: a.apy7d ?? null,
      apy14d: a.apy14d ?? null,
      apy30d: a.apy30d ?? null,
    }));
    if (chainId !== undefined) {
      return assets.filter((a) => a.chainId === chainId);
    }
    return assets;
  }

  async getAgentMessages(
    walletAddress: string,
    page = 1,
    limit = 20,
    from?: string,
    to?: string,
  ): Promise<AgentMessagesResult> {
    let path = `/api/v4/agent-messages?walletAddress=${walletAddress}&page=${page}&limit=${limit}`;
    if (from) {
      path += `&from=${encodeURIComponent(from)}`;
    }
    if (to) {
      path += `&to=${encodeURIComponent(to)}`;
    }
    const response = await this.http.get<any>(path);
    const data = response.data ?? response;
    return {
      page: data.page ?? page,
      limit: data.limit ?? limit,
      total: data.total ?? 0,
      pages: data.pages ?? 1,
      messages: data.messages ?? [],
    };
  }

  async getBestVault(assetSymbol: string): Promise<BestVaultOption[]> {
    const response = await this.http.get<any>(
      `/api/v4/vaults/best?assetSymbol=${encodeURIComponent(assetSymbol)}`,
    );
    return response.vaults ?? response.data ?? response ?? [];
  }
}
